import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { LogIn } from 'lucide-react';

/*
 * Hover / click "description bubbles" for the landing page's workspace preview.
 *
 * Every inert control binds itself with `bind(key, { title, description })`: hovering shows the bubble under the
 * control, clicking switches the bubble to its "nudge" form ("Sign in to try this") and pulses the sign-in card once, so
 * a dead end becomes an invitation instead of feeling broken. Controls that really work on the landing page (template
 * thumbnails, the sign-in box's handles) bind with `{ live: true }`: they get the hover bubble only — a click is theirs.
 * One bubble element serves the whole page (there are ~70 controls); it is positioned from the hovered control's
 * bounding box and flips above it near the bottom edge. `suspend(true)` hides it and ignores hovers while dragging.
 */

const DemoHintContext = createContext(null);
const noop = () => {};
export const useDemoHint = () => useContext(DemoHintContext) || { bind: () => ({}), pulse: false, nudgeTo: noop, dismiss: noop, suspend: noop };

const HOVER_DELAY = 140;
const NUDGE_LIFETIME = 4200;
const PULSE_LIFETIME = 950;
const TAP_LIFETIME = 2600;

export const DemoHintProvider = ({ children }) => {
  const [hint, setHint] = useState(null); // { key, title, description, rect, nudge }
  const [pulse, setPulse] = useState(false);
  const timers = useRef({ show: null, hide: null, nudge: null, pulse: null, tap: null });
  const suspended = useRef(false);

  const clear = (name) => { if (timers.current[name]) { clearTimeout(timers.current[name]); timers.current[name] = null; } };
  const rectOf = (el) => { const r = el.getBoundingClientRect(); return { left: r.left, top: r.top, width: r.width, height: r.height, bottom: r.bottom }; };

  const dismiss = useCallback(() => { clear('show'); clear('hide'); clear('tap'); setHint(null); }, []);
  const suspend = useCallback((on) => { suspended.current = !!on; if (on) { clear('show'); clear('hide'); clear('tap'); setHint(null); } }, []);

  const nudgeTo = useCallback((el, meta) => {
    clear('show'); clear('hide'); clear('nudge'); clear('pulse'); clear('tap');
    setHint({ ...meta, rect: rectOf(el), nudge: true });
    setPulse(true);
    timers.current.pulse = setTimeout(() => setPulse(false), PULSE_LIFETIME);
    timers.current.nudge = setTimeout(() => setHint((h) => (h && h.nudge ? null : h)), NUDGE_LIFETIME);
  }, []);

  const bind = useCallback((key, meta, { live = false } = {}) => ({
    onMouseEnter: (e) => {
      if (suspended.current) return;
      const el = e.currentTarget;
      clear('hide');
      clear('show');
      timers.current.show = setTimeout(() => setHint((h) => (h && h.nudge && h.key === key ? h : { key, ...meta, rect: rectOf(el), nudge: false })), HOVER_DELAY);
    },
    onMouseLeave: () => {
      clear('show');
      clear('hide');
      timers.current.hide = setTimeout(() => setHint((h) => (h && h.key === key && !h.nudge ? null : h)), 90);
    },
    onClick: live
      ? (e) => {
        // a tap (no hover on touch screens) shows the plain bubble for a moment; the control's own onClick still runs
        if (suspended.current || e.detail === 0) return;
        clear('show'); clear('tap');
        setHint({ key, ...meta, rect: rectOf(e.currentTarget), nudge: false });
        timers.current.tap = setTimeout(() => setHint((h) => (h && h.key === key && !h.nudge ? null : h)), TAP_LIFETIME);
      }
      : (e) => { e.preventDefault(); nudgeTo(e.currentTarget, { key, ...meta }); },
    'data-demo-hint': key,
  }), [nudgeTo]);

  // anything that moves the controls invalidates the bubble's position
  useEffect(() => {
    const hide = () => setHint((h) => (h && h.nudge ? h : null));
    const pending = timers.current; // the same object for the component's whole life
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => { window.removeEventListener('scroll', hide, true); window.removeEventListener('resize', hide); Object.values(pending).forEach((t) => t && clearTimeout(t)); };
  }, []);

  const focusSignIn = () => {
    setHint(null);
    const el = document.getElementById('auth-email') || document.getElementById('auth-forgot-email');
    el?.focus({ preventScroll: false });
    el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  };

  return (
    <DemoHintContext.Provider value={{ bind, pulse, nudgeTo, dismiss, suspend }}>
      {children}
      <DemoBubble hint={hint} onSignIn={focusSignIn} onDismiss={() => setHint(null)} />
    </DemoHintContext.Provider>
  );
};

const DemoBubble = ({ hint, onSignIn, onDismiss }) => {
  if (!hint) return null;
  const vw = window.innerWidth, vh = window.innerHeight;
  const width = Math.min(272, vw - 24);
  const cx = Math.min(Math.max(hint.rect.left + hint.rect.width / 2, width / 2 + 12), vw - width / 2 - 12);
  const below = hint.rect.bottom + 10 + 120 < vh;
  const style = below
    ? { left: cx, top: hint.rect.bottom + 10, transform: 'translateX(-50%)', width }
    : { left: cx, bottom: vh - hint.rect.top + 10, transform: 'translateX(-50%)', width };
  const arrowLeft = Math.min(Math.max(hint.rect.left + hint.rect.width / 2 - (cx - width / 2), 14), width - 14);
  return (
    <div
      role={hint.nudge ? undefined : 'tooltip'}
      aria-live="polite"
      className={`fixed z-[90] rounded-lg border border-border bg-popover/95 text-popover-foreground shadow-lg backdrop-blur-md px-3 py-2.5 text-xs ${hint.nudge ? '' : 'pointer-events-none'} animate-in fade-in zoom-in-95 duration-150`}
      style={style}
      data-nudge={hint.nudge ? 'true' : undefined}
      data-testid="demo-bubble"
    >
      <span aria-hidden="true" className={`absolute h-2.5 w-2.5 rotate-45 bg-popover border-border ${below ? '-top-[6px] border-l border-t' : '-bottom-[6px] border-r border-b'}`} style={{ left: arrowLeft - 5 }} />
      <div className="font-semibold text-[12px] text-foreground" data-testid="demo-bubble-title">{hint.title}</div>
      {hint.description && <div className="mt-0.5 text-muted-foreground leading-snug" data-testid="demo-bubble-description">{hint.description}</div>}
      {hint.nudge && (
        <div className="mt-2 pt-2 border-t border-border/70 flex items-center justify-between gap-2">
          <span className="text-muted-foreground">This is a preview — the real thing is one sign-in away.</span>
          <button type="button" onClick={onSignIn} onMouseDown={(e) => e.preventDefault()} className="inline-flex items-center gap-1 shrink-0 h-7 rounded-md bg-primary px-2.5 text-[11px] font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="demo-bubble-signin"><LogIn className="h-3 w-3" aria-hidden="true" />Sign in to try this</button>
          <button type="button" onClick={onDismiss} className="sr-only">Dismiss</button>
        </div>
      )}
    </div>
  );
};
